import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import { TrashIcon } from '@heroicons/react/outline'
import { analytics } from 'analytics'
import { isNull } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { NumberFormatValues, NumericFormat } from 'react-number-format'

import { HttpStatus } from 'enums'
import { Asset, PortfolioLinkType } from 'types'

import { paveApi } from 'api'
import ModalLoader from 'components/common/Loader/ModalLoader'
import AddAssetForm from 'components/partials/Portfolio/V2/AddAssetForm'
import { PORTFOLIO_EDIT_ASSETS_SUBMITTED } from 'constants/track.constants'
import PDSAssetTag from 'designSystem/components/PDSAssetTag/PDSAssetTag'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSLinkWrapper from 'designSystem/components/PDSLinkWrapper.tsx/PDSLinkWrapper'
import PDSSeparator from 'designSystem/components/PDSSeparator/PDSSeparator'
import PDSSubCard from 'designSystem/components/PDSSubCard/PDSSubCard'
import PDSText from 'designSystem/components/PDSText/PDSText'
import PDSTextInput from 'designSystem/components/PDSTextInput/PDSTextInput'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_WEIGHT } from 'designSystem/foundations/constants/fontWeight'
import { SIZE } from 'designSystem/foundations/constants/size'
import { useCustomToast } from 'hooks/useCustomToast'
import { closeEditPortfolioAssets } from 'store/modalsSlice'
import { useAppDispatch, useAppSelector } from 'store/storeHooks'
import { getPortfolioLinkType } from 'utils/portfolioUtil'

const EditHoldingsModal = () => {
  const dispatch = useAppDispatch()
  const { showEditPortfolioAssets, editingPortfolioAssets } = useAppSelector(
    ({ modals }) => modals
  )

  const { showErrorToast, showSuccessToast, showViewOnlyDefaultToast } =
    useCustomToast()
  const [editingAssets, setEditingAssets] = useState<Asset[] | null>(null)
  const [showForm, setShowForm] = useState<boolean>(false)
  const [editingAvailableCash, setEditingAvailableCash] = useState<
    number | null
  >(null)
  const [hasDoneChanges, setHasDoneChanges] = useState<boolean>(false)
  const [editHoldings, editHoldingsResult] = paveApi.useEditHoldingsMutation()
  const handleClose = useCallback(() => {
    dispatch(closeEditPortfolioAssets())
    setHasDoneChanges(false)
  }, [dispatch])

  const linkType = getPortfolioLinkType(editingPortfolioAssets)
  const readOnly = linkType !== PortfolioLinkType.None

  useEffect(() => {
    setEditingAssets(editingPortfolioAssets?.assets || [])
    setEditingAvailableCash(editingPortfolioAssets?.availableCash || 0)
  }, [editingPortfolioAssets])

  const handleSubmit = () => {
    if (
      isNull(editingPortfolioAssets) ||
      isNull(editingAssets) ||
      isNull(editingAvailableCash)
    ) {
      showErrorToast('An error occurred updating your assets')
      return
    }

    editHoldings({
      id: editingPortfolioAssets.id,
      body: {
        assets: editingAssets,
        availableCash: editingAvailableCash,
      },
    })
      .unwrap()
      .then(() => {
        showSuccessToast('Assets updated!')
        handleClose()
      })
      .catch((e) => {
        if (e?.status === HttpStatus.FORBIDDEN) {
          showViewOnlyDefaultToast()
        } else {
          showErrorToast('An error occurred updating your assets')
        }
      })
    analytics.track(PORTFOLIO_EDIT_ASSETS_SUBMITTED)
  }

  const handleAddAssets = useCallback(
    (newAssets: Asset[]) => {
      setEditingAssets(newAssets)
      if (!hasDoneChanges) {
        setHasDoneChanges(true)
      }
    },
    [hasDoneChanges]
  )

  const handleAdd = (newAsset: Asset) => {
    setShowForm(false)

    if (!editingAssets) return

    if (editingAssets.some((asset) => asset.symbol === newAsset.symbol)) {
      return
    }
    handleAddAssets([...editingAssets, newAsset])
  }
  const handleAvailableCashChange = (v: NumberFormatValues) => {
    const value: number = typeof v.floatValue === 'number' ? v.floatValue : 0
    setEditingAvailableCash(value)
    if (!hasDoneChanges) {
      setHasDoneChanges(true)
    }
  }

  return (
    <Modal
      isCentered
      isOpen={showEditPortfolioAssets}
      onClose={handleClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader />
        <ModalCloseButton />
        <ModalBody paddingX={0}>
          {editHoldingsResult.isLoading ? (
            <ModalLoader />
          ) : (
            <PDSFlexBox flexDirection="column">
              <PDSFlexBox
                flexDirection="column"
                gap={SIZE.s16}
                paddingX={SIZE.s32}
                paddingBottom={SIZE.s16}
              >
                <PDSText
                  size="lg"
                  color={COLOR.textPrimary}
                  fontWeight={FONT_WEIGHT.semibold}
                >
                  Edit Positions
                </PDSText>
                <PDSFlexBox flexDirection="column" gap={SIZE.s24}>
                  <PDSFlexBox flexDirection="column" gap={SIZE.s16}>
                    <PDSText
                      size="md"
                      color={COLOR.textSecondary}
                      fontWeight={FONT_WEIGHT.medium}
                    >
                      Available Cash
                    </PDSText>
                    <NumericFormat
                      allowNegative={false}
                      color={COLOR.textSecondary}
                      customInput={PDSTextInput}
                      decimalScale={2}
                      disabled={readOnly}
                      displayType="input"
                      onValueChange={handleAvailableCashChange}
                      placeholder="$0.00"
                      prefix="$"
                      thousandSeparator={true}
                      value={editingAvailableCash}
                    />
                  </PDSFlexBox>
                </PDSFlexBox>
                <PDSFlexBox
                  justifyContent="space-between"
                  gap={SIZE.s12}
                  width="78%"
                >
                  <PDSBox flexBasis="1rem">
                    <PDSText color={COLOR.textTertiary} weight="medium">
                      Symbol
                    </PDSText>
                  </PDSBox>

                  <PDSBox flexBasis="0.5rem">
                    <PDSText color={COLOR.textTertiary} weight="medium">
                      Shares
                    </PDSText>
                  </PDSBox>
                  <PDSBox flexBasis="0.5rem" />
                </PDSFlexBox>
                <PDSFlexBox
                  flexDirection="column"
                  gap={SIZE.s8}
                  maxHeight="18.75rem"
                  overflowY="scroll"
                >
                  {editingAssets?.map((asset) => (
                    <PDSLinkWrapper>
                      <PDSSubCard
                        alignItems="center"
                        display="flex"
                        gap={SIZE.s8}
                        justifyContent="space-between"
                        paddingSize="sm"
                        alignSelf="stretch"
                      >
                        <PDSFlexBox
                          alignItems="center"
                          justifyContent="space-between"
                          gap={SIZE.s16}
                          width="100%"
                        >
                          <PDSAssetTag
                            marginRight={SIZE.s8}
                            symbol={asset.symbol}
                          />
                          <PDSFlexBox
                            alignItems="center"
                            alignSelf="start"
                            width="5rem"
                          >
                            <PDSText hideOverflow={true} weight="medium">
                              {asset.shares}
                            </PDSText>
                          </PDSFlexBox>
                          <PDSFlexBox
                            justifyContent="center"
                            alignItems="start"
                            width="3.75rem"
                          >
                            <TrashIcon width={20} height={20} />
                          </PDSFlexBox>
                        </PDSFlexBox>
                      </PDSSubCard>
                    </PDSLinkWrapper>
                  ))}
                </PDSFlexBox>
                <PDSFlexBox flexDirection="row">
                  {showForm ? (
                    <AddAssetForm
                      scrollDown={() => {}}
                      handleAdd={handleAdd}
                      handleCancel={() => setShowForm(false)}
                    />
                  ) : (
                    <PDSButton
                      text="Add Another Asset"
                      iconLeft="plus"
                      size="sm"
                      variant="tertiary"
                      onClick={() => setShowForm(true)}
                    />
                  )}
                </PDSFlexBox>
              </PDSFlexBox>
            </PDSFlexBox>
          )}
        </ModalBody>
        <PDSSeparator marginTop={SIZE.s16} />
        <ModalFooter paddingTop={SIZE.s12}>
          <PDSFlexBox
            flexDirection="column"
            padding={SIZE.s32}
            gap={SIZE.s8}
            width="100%"
          >
            <PDSButton
              text="Save changes"
              type="submit"
              onClick={handleSubmit}
            />
            <PDSButton text="Cancel" variant="tertiary" onClick={handleClose} />
          </PDSFlexBox>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default EditHoldingsModal
